/**
 * API Keys Management API module for Platform Access
 * Provides functions to create, manage, and monitor API keys for programmatic access
 * 
 * NOTE: This is a frontend implementation. The backend /v1/api-keys endpoint 
 * needs to be implemented to handle these requests.
 */

import { api, generateCRUDInterface, getCurrentUser } from "aldoo-ra/API"

// Use CRUD endpoint for API Keys operations
const crudAPI = api("crud")
const apiKeysCRUD = generateCRUDInterface(crudAPI, "APIKey")

// Keep specialized endpoint for statistics (admin only)
const apiKeysAPI = api("api-keys")

// Helper function to handle API errors consistently
const handleAPIError = (error, operation) => {
  console.error(`Failed to ${operation}:`, error)
  
  // Check if this is a backend not implemented error
  if (error.response?.data?.error === "system_error" || error.response?.status === 404) {
    return {
      result: false,
      error: "API Keys feature requires backend implementation. Please contact your system administrator to implement the /v1/api-keys endpoint."
    }
  }
  
  return {
    result: false,
    error: error.message || `Failed to ${operation}`
  }
}

export const APIKeysPlatformAPI = {
  
  /**
   * Create a new API key
   * @param {Object} data - API key creation data
   * @param {string} data.description - Human readable description
   * @param {string} data.targetUserId - Target user ID for the API key
   * @param {string} [data.validUntil] - Optional expiration date (ISO string)
   * @param {Array<string>} [data.ipWhitelist] - Optional array of allowed IP addresses
   * @param {Object} [data.rateLimit] - Optional rate limit configuration
   * @returns {Promise<{result: boolean, key?: string, apiKey?: Object, error?: string}>}
   */
  CreateAPIKey: async (data) => {
    try {
      // Get current user credentials
      const currentUser = getCurrentUser()

      const createData = {
        targetUserId: data.targetUserId,
        description: data.description,
        ipWhitelist: data.ipWhitelist || []
      }

      // Add current user as issuer if available
      if (currentUser?._id) {
        createData.issuedBy = currentUser._id
      }

      // Explicitly handle validUntil to allow null values
      if (data.validUntil !== undefined) {
        createData.validUntil = data.validUntil
      }

      // Explicitly handle rateLimit to allow null values
      if (data.rateLimit !== undefined) {
        createData.rateLimit = data.rateLimit
      }

      // Use specialized endpoint for creation (handles key generation and validation)
      const response = await apiKeysAPI({
        create: createData
      })

      return {
        result: response.result,
        key: response.key,
        apiKey: response.apiKey,
        error: response.error
      }
    } catch (error) {
      return handleAPIError(error, "create API key")
    }
  },

  /**
   * List API keys with optional filtering
   * @param {Object} [filters={}] - Filter options
   * @param {string} [filters.status] - Filter by status: "active", "expired", "revoked", "disabled"
   * @param {string} [filters.issuedBy] - Filter by issuer
   * @param {Array<string>} [filters.permissions] - Filter by permissions
   * @param {number} [filters.limit=50] - Number of results per page (max 100)
   * @param {number} [filters.page=1] - Page number
   * @returns {Promise<{result: boolean, keys: Array, total: number, page: number, limit: number, error?: string}>}
   */
  ListAPIKeys: async (filters = {}) => {
    try {
      const match = {}
      if (filters.status) match.status = filters.status
      if (filters.issuedBy) match.issuedBy = filters.issuedBy
      if (filters.userId) match.user = filters.userId

      const getOptions = {
        match,
        page: filters.page || 1,
        itemsPerPage: filters.limit || 50,
        select: "key description status validUntil lastUsedAt createdAt updatedAt user issuedBy revokedAt revokedBy ipWhitelist rateLimit meta",
        sort: { createdAt: -1 },
        populate: [
          { path: "user", select: "name email" },
          { path: "issuedBy", select: "name email" },
          { path: "revokedBy", select: "name email" }
        ]
      }

      const response = await apiKeysCRUD.Get(getOptions)

      return {
        result: true,
        keys: response?.data || [],
        total: response?.totalItems || 0,
        page: filters.page || 1,
        limit: filters.limit || 50,
        error: null
      }
    } catch (error) {
      const errorResult = handleAPIError(error, "list API keys")
      return {
        ...errorResult,
        keys: [],
        total: 0,
        page: 1,
        limit: 50
      }
    }
  },

  /**
   * Get API key details
   * @param {string} key - The API key (UUID)
   * @returns {Promise<{result: boolean, apiKey?: Object, error?: string}>}
   */
  GetAPIKey: async (key) => {
    try {
      const response = await apiKeysAPI({
        get: {
          key: key
        }
      })
      
      return {
        result: response.result,
        apiKey: response.apiKey,
        error: response.error
      }
    } catch (error) {
      return handleAPIError(error, "get API key details")
    }
  },

  /**
   * Update an existing API key
   * @param {Object} data - Update data
   * @param {string} data.key - The API key (UUID)
   * @param {string} [data.description] - New description
   * @param {string|null} [data.validUntil] - New expiration date or null to remove expiration
   * @param {Array<string>} [data.ipWhitelist] - New IP whitelist
   * @param {Object} [data.rateLimit] - New rate limit configuration
   * @param {string} [data.status] - New status: 'active', 'disabled', or 'revoked'
   * @returns {Promise<{result: boolean, apiKey?: Object, error?: string}>}
   */
  UpdateAPIKey: async (data) => {
    try {
      const updateData = {}

      if (data.description !== undefined) updateData.description = data.description
      if (data.validUntil !== undefined) updateData.validUntil = data.validUntil
      if (data.ipWhitelist !== undefined) updateData.ipWhitelist = data.ipWhitelist
      if (data.rateLimit !== undefined) updateData.rateLimit = data.rateLimit

      // Handle status changes with revocation tracking
      if (data.status !== undefined) {
        updateData.status = data.status

        const currentUser = getCurrentUser()

        if (data.status === 'revoked') {
          // Set revocation details when revoking
          updateData.revokedAt = new Date().toISOString()
          if (currentUser?._id) {
            updateData.revokedBy = currentUser._id
          }
        } else if (data.status === 'active') {
          // Clear revocation details when reactivating
          updateData.revokedAt = null
          updateData.revokedBy = null
        }
      }

      // Use key to find the API key to update
      const match = { key: data.key }

      const response = await crudAPI({
        update: {
          model: "APIKey",
          match,
          data: updateData
        }
      })

      return {
        result: response.result || true,
        apiKey: response.updated || response,
        error: response.error || null
      }
    } catch (error) {
      return handleAPIError(error, "update API key")
    }
  },

  /**
   * Revoke an API key
   * @param {string} key - The API key (UUID) to revoke
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  RevokeAPIKey: async (key) => {
    try {
      const response = await apiKeysAPI({
        revoke: {
          key: key
        }
      })
      
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      return handleAPIError(error, "revoke API key")
    }
  },

  /**
   * Hard delete an API key (permanent removal)
   * @param {string} keyId - The API key ID to delete
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  HardDeleteAPIKey: async (keyId) => {
    try {
      const response = await apiKeysCRUD.HardDelete({ _id: keyId })

      return {
        result: response.result || true,
        error: response.error || null
      }
    } catch (error) {
      return handleAPIError(error, "delete API key")
    }
  },

  /**
   * Get API keys statistics
   * @returns {Promise<{result: boolean, stats?: Object, error?: string}>}
   */
  GetStats: async () => {
    try {
      const response = await apiKeysAPI({
        stats: {}
      })

      return {
        result: response.result,
        stats: response.stats,
        error: response.error
      }
    } catch (error) {
      return handleAPIError(error, "get API keys statistics")
    }
  }
}

export default APIKeysPlatformAPI